import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, ChevronDown } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const interestLabel = (lang, label) =>
  lang === "hi" ? `मुझे रुचि है: ${label}` : `I'm Interested in ${label}`;

// Web (desktop) header CTA: an "I'm Interested" pill that opens a dropdown of
// all four paths. Each option opens the GiriChat WhatsApp deep-link with the
// path's segmentation tag.
export const InterestButton = ({ options }) => {
  const { lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  if (!options?.length) return null;
  return (
    <div ref={ref} className="relative hidden sm:block" data-testid="interest-button-web">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-2 text-xs font-semibold text-[#07240f] shadow-lg transition-all hover:brightness-110"
      >
        <MessageCircle className="h-4 w-4" />
        {lang === "hi" ? "मुझे रुचि है" : "I'm Interested"}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className="absolute right-0 mt-2 w-64 overflow-hidden rounded-xl border border-amber-300/20 bg-[#0d1226] p-1 shadow-2xl"
          >
            {options.map((o) => (
              <a
                key={o.key}
                href={o.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`interest-option-${o.key}`}
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-amber-100/80 transition-colors hover:bg-amber-400/10 hover:text-amber-100"
              >
                <MessageCircle className="h-3.5 w-3.5 text-[#25D366]" />
                {interestLabel(lang, o.label)}
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Mobile: a sticky bottom bar that targets the journey currently in view.
export const MobileInterestBar = ({ option }) => {
  const { lang } = useLanguage();
  if (!option) return null;
  return (
    <motion.div
      initial={{ y: 90, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 260, damping: 26 }}
      data-testid="interest-bar-mobile"
      className="fixed inset-x-0 bottom-0 z-40 border-t border-amber-300/20 bg-[#070b18]/92 p-3 backdrop-blur-md sm:hidden"
    >
      <a
        href={option.href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="interest-bar-mobile-btn"
        className="flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3.5 text-sm font-semibold text-[#07240f] shadow-lg"
      >
        <MessageCircle className="h-4 w-4" />
        {interestLabel(lang, option.label)}
      </a>
    </motion.div>
  );
};
